import type { QueryClient } from '@tanstack/react-query';
import { ApiError, request } from '@/shared/api/client';
import type { Message, Thread } from '@/shared/api/schemas';
import { isOnline, setOnline } from '@/shared/offline/network';
import { enqueue, flushOutbox, pending, type OutboxItem } from '@/shared/offline/outbox';
import { rememberSent, restoreToComposer } from '@/features/composer/unsend';
import { keys } from './keys';
import { removeMessage, upsertMessages, type LocalMessage, type LocalPage } from './localMessages';
import { patchSidebar, sentSchema, type SendInput } from './api';

/** Отправка не ушла: нет связи, сообщение лежит в очереди на устройстве и уйдёт, когда она появится. */
export class QueuedOffline extends Error {
  override readonly name = 'QueuedOffline';
}

/** Запрос не дошёл до сервера (а не получил отказ): сеть пропала, сервер не отвечает. */
export function isNetworkFailure(error: unknown): boolean {
  // Страница входа в Wi-Fi вместо ответа сервера: не JSON — значит, до сервера не дошли.
  return error instanceof TypeError || error instanceof SyntaxError;
}

/** Отказ, после которого повтор бессмысленен: сообщение не отправится никогда. */
export function isPermanent(error: unknown): boolean {
  // 401 — сеанс закончился: сообщение ждёт нового входа, а не пропадает.
  return error instanceof ApiError && error.status >= 400 && error.status < 500 && ![401, 408, 429].includes(error.status);
}

export function sendBody(input: SendInput): Record<string, unknown> {
  return {
    content: input.content,
    threadRootId: input.threadRootId,
    alsoInChannel: input.alsoInChannel,
    attachmentIds: input.attachments.map((a) => a.id),
    clientId: input.clientId,
    ...(input.collab === true ? { collab: true } : {}),
    ...(input.quote === undefined ? {} : { quoteMessageId: input.quote.messageId, quoteText: input.quote.text }),
    dlpAck: input.dlpAck ?? [],
    ...(input.linkPreview != null ? { linkPreview: input.linkPreview } : {}),
  };
}

/** Положить сообщение в очередь на устройстве. */
export async function queueSend(workspaceId: string, userId: string, api: string, input: SendInput): Promise<never> {
  await enqueue({ id: input.clientId, workspaceId, userId, payload: { url: `${api}/conversations/${input.cid}/messages`, input }, queuedAt: Date.now() });
  throw new QueuedOffline();
}

/** Если запрос не дошёл до сервера — считаем, что связи нет, и откладываем сообщение. */
export async function sendOrQueue<T>(workspaceId: string, userId: string, api: string, input: SendInput, run: () => Promise<T>): Promise<T> {
  // Очередь не пуста — новое сообщение встаёт за ней, чтобы порядок не перепутался.
  if (!isOnline() || (await pending(workspaceId)).length > 0) return queueSend(workspaceId, userId, api, input);
  try {
    return await run();
  } catch (error) {
    if (!isNetworkFailure(error)) throw error;
    setOnline(false);
    return queueSend(workspaceId, userId, api, input);
  }
}

export function placeLocal(client: QueryClient, wid: string, input: SendInput, message: LocalMessage | Message): void {
  if (input.threadRootId === null || input.alsoInChannel) {
    client.setQueryData<LocalPage>(keys.messages(wid, input.cid), (old) => (old === undefined ? old : upsertMessages(old, [message], input.clientId)));
  }
  if (input.threadRootId !== null) {
    client.setQueryData<Thread>(keys.thread(wid, input.threadRootId), (old) =>
      old === undefined ? old : { ...old, replies: [...old.replies.filter((r) => r.id !== message.id && !('clientId' in r && r.clientId === input.clientId)), message] },
    );
  }
}

export function discardLocal(client: QueryClient, wid: string, input: SendInput): void {
  client.setQueryData<LocalPage>(keys.messages(wid, input.cid), (old) => (old === undefined ? old : removeMessage(old, input.clientId)));
  if (input.threadRootId !== null) {
    client.setQueryData<Thread>(keys.thread(wid, input.threadRootId), (old) => (old === undefined ? old : { ...old, replies: old.replies.filter((r) => r.id !== input.clientId) }));
  }
}

export function markLocal(client: QueryClient, wid: string, input: SendInput, status: 'queued' | 'failed'): void {
  const mark = (m: LocalMessage): LocalMessage => (m.clientId === input.clientId ? { ...m, status } : m);
  client.setQueryData<LocalPage>(keys.messages(wid, input.cid), (old) => (old === undefined ? old : { ...old, messages: old.messages.map(mark) }));
  if (input.threadRootId !== null) {
    client.setQueryData<Thread>(keys.thread(wid, input.threadRootId), (old) => (old === undefined ? old : { ...old, replies: old.replies.map(mark) }));
  }
}

/** Сервер принял сообщение (сразу или после очереди): настоящее вместо черновика. */
export function settleSent(client: QueryClient, wid: string, input: SendInput, message: Message): void {
  rememberSent(message.id, input.cid, input.threadRootId);
  placeLocal(client, wid, input, message);
  patchSidebar(client, wid, input.cid, (c) => ({ ...c, lastSeq: Math.max(c.lastSeq, message.seq), lastReadSeq: Math.max(c.lastReadSeq, message.seq), hidden: c.kind === 'public' || c.kind === 'private' ? c.hidden : false, lastMessageAt: message.createdAt }));
}

export interface QueuedPayload {
  readonly url: string;
  readonly input: SendInput;
}

function isSendInput(value: unknown): value is SendInput {
  return typeof value === 'object' && value !== null && typeof Reflect.get(value, 'clientId') === 'string' && typeof Reflect.get(value, 'cid') === 'string' && Array.isArray(Reflect.get(value, 'attachments'));
}

export function readPayload(item: OutboxItem): QueuedPayload | null {
  const p = item.payload;
  if (typeof p !== 'object' || p === null) return null;
  const url: unknown = Reflect.get(p, 'url');
  const input: unknown = Reflect.get(p, 'input');
  return typeof url === 'string' && isSendInput(input) ? { url, input } : null;
}

const sendRequest = (payload: QueuedPayload) => request('POST', payload.url, sentSchema, { body: sendBody(payload.input) });

let flushing = false;

/** Отправить всё, что накопилось без связи, по порядку. Возвращает, сколько ушло. */
export async function flushQueued(client: QueryClient): Promise<number> {
  if (flushing) return 0;
  flushing = true;
  try {
    return await flushOutbox(null, async (item) => {
      const payload = readPayload(item);
      if (payload === null) return 'drop';
      let result: Awaited<ReturnType<typeof sendRequest>>;
      try {
        result = await sendRequest(payload);
      } catch (error) {
        if (isNetworkFailure(error)) {
          setOnline(false);
          return 'retry';
        }
        if (!isPermanent(error)) return 'retry';
        markLocal(client, item.workspaceId, payload.input, 'failed');
        // Текст не теряется: возвращаем его в поле ввода беседы.
        restoreToComposer(payload.input.cid, payload.input.threadRootId, { content: payload.input.content, text: payload.input.text, attachments: payload.input.attachments });
        return 'drop';
      }
      if ('held' in result) discardLocal(client, item.workspaceId, payload.input);
      else settleSent(client, item.workspaceId, payload.input, result);
      return 'sent';
    });
  } finally {
    flushing = false;
  }
}
